<template>
  <div>
    <!-- DataTable to display transactions with pagination and custom columns. -->
    <PvDataTable :value="transactions" size="small" :paginator="showPagination" :rows=10 >
      <!-- Action column with a button to view or edit transactions. -->
      <PvColumn header="Action" class="action-column">
        <template #body="slotProps">
          <PvButton @click="viewTransaction(slotProps.data.transactionId)"
                    size="small"
                    severity="secondary"
                    :title="buttonProperties.title"
                    class="action-btn"
                    :icon="buttonProperties.icon"
                    rounded raised outlined />     
        </template>
      </PvColumn>
      <!-- Column to display the transaction title. -->
      <PvColumn field="transactionTitle" header="Transaction Title" />
      <!-- Column to display the transaction type description. -->
      <PvColumn field="transactionTypeDescription" header="Transaction Type" />
      <!-- Column to display the proposed completion date with formatted value. -->
      <PvColumn field="proposedCompletionDate" header="Proposed Completion Date" class="date-column">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.proposedCompletionDate) }}
        </template>
      </PvColumn>
      <!-- Column to display the due date with formatted value. -->
      <PvColumn field="dueDate" header="Due Date" class="date-column">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.dueDate) }}
        </template>
      </PvColumn>
      <!-- Column to display the transaction status description. -->
      <PvColumn field="transactionStatusDescription" header="Status" />

      <!-- Template to display when there are no transactions. -->
      <template #empty>
        <NoData message="No transactions." />
      </template>  
    </PvDataTable>
  </div>
</template>

<script setup>
// Importing required modules and components.
import { onMounted, ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { ICONS } from '@/utils/constants';
import NoData from '../common/NoData.vue';
import { formatDate } from '@/utils/validationUtils';

// Initializing the router for navigation.
const router = useRouter();
// Reactive variable to control pagination visibility.
const showPagination = ref(false);

// Defining component props.
const props = defineProps({
  transactions: {
    type: Array,
    default: null,
    required: true
  },
  isTransactionsCompleted: {
    type: Boolean,
    default: false,
    required: false
  }
});

// Function to navigate to the transaction details page.
const viewTransaction = (transactionId) => {
  if (transactionId) {
    router.push({ name: 'edittransaction', params: { id: transactionId } });
  } 
};

// Computed property to determine button properties based on transaction status.
const buttonProperties = computed (() => {
  return {
    title: props.isTransactionsCompleted ? 'View the transaction details.' : 'Edit the transaction details.',
    icon: props.isTransactionsCompleted ? ICONS.View :ICONS.Edit,
  };
});

// Function to update pagination visibility based on the number of transactions.
const updatePagination = () => {
  if (props.transactions && props.transactions.length > 10) {
    showPagination.value = true;
  } else {
    showPagination.value = false;
  }
};

// Lifecycle hook to initialize pagination visibility on component mount.
onMounted(() => {
  updatePagination();
});

// Watcher to update pagination visibility when transactions change.
watch(() => props.transactions, () => {
  updatePagination();
});
</script>